import { selectIsPremium } from "domain/auth/auth.selectors";
import {
  ActivitiesPageFilters,
  DurationFilter,
} from "domain/entities/activities.entities";
import { Tutorial } from "domain/entities/tutorials.entities";
import { SetStateAction, Dispatch, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { Snackbar } from "ui/pages/snackbar";
import { useWindowWidth } from "ui/utils/hooks";
import { MobileActivitiesFilters } from "ui/pages/app/components/activities/MobileActivitiesFilters";
import { PageHeader } from "ui/pages/app/components/PageHeader";
import { translate } from "ui/i18n";
import { DesktopActivitiesFilters } from "ui/pages/app/components/activities/DesktopActivitiesFilters";
import { INSearchField } from "ui/pages/app/components/inputs/INSearchField";
import { HiAdjustments } from "react-icons/hi";
import { getFiltersCount } from "ui/utils/get-filters-count";
import {
  getTutorials,
  setIsTutorialLoading,
} from "domain/tutorials/tutorials.actions";
import {
  selectTutorials,
  selectTutorialsTotalCount,
} from "domain/tutorials/tutorials.selectors";
import { TutorialCard } from "ui/pages/app/components/activities/tutorials/TutorialCard";
import { INSkeleton } from "ui/pages/app/components/inputs/INSkeleton";

export function Tutorials({
  filters,
  setFilters,
}: {
  filters: ActivitiesPageFilters & {
    [key: string]: string | string[] | boolean | DurationFilter | number;
  };
  setFilters: Dispatch<
    SetStateAction<
      ActivitiesPageFilters & {
        [key: string]: string | string[] | boolean | number | DurationFilter;
      }
    >
  >;
}) {
  const dispatch = useAppDispatch();
  const isPremium = useAppSelector(selectIsPremium);
  const tutorials = useAppSelector(selectTutorials);
  const totalCount = useAppSelector(selectTutorialsTotalCount);
  const navigate = useNavigate();
  const isDesktop = useWindowWidth();
  const [search, setSearch] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTutorials = async () => {
    setIsLoading(true);
    await dispatch(
      getTutorials({
        search,
        ...filters,
      })
    );
    setIsLoading(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchTutorials();
    }, 500);
    return () => clearTimeout(timeout);
  }, [filters, search]); //eslint-disable-line

  const handleCardClick = async (tutorial: Tutorial) => {
    if (!isPremium && !!tutorial.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "tutorial");
    } else {
      await dispatch(setIsTutorialLoading(true));
      navigate(`/tutorials?id=${tutorial?.id}`);
    }
  };

  const resetFilters = () => {
    setFilters({
      categories: [],
    });
  };

  return (
    <div
      className={`flex flex-row overflow-x-hidden ${
        isFiltersOpen ? "filters-open" : "filters-closed"
      }`}
      style={{ width: isDesktop ? "100%" : "200vw" }}
    >
      <div className="w-screen px-2 pt-2 md:px-12 md:pt-8">
        <PageHeader
          search={search}
          setSearch={setSearch}
          title={translate("ns1:Routes.Tutorials")}
          placeholder="TutorialName"
          subTitle={translate("ns1:Tutorials.Subtitle")}
          activityCount={totalCount}
        />

        {isDesktop ? (
          <DesktopActivitiesFilters
            filters={filters}
            setFilters={setFilters}
            fetchActivities={fetchTutorials}
            activitiesType="tutorials"
            resetFilters={resetFilters}
          />
        ) : (
          <div className="mt-7 flex">
            <INSearchField
              placeholder={translate("ns1:Input.Placeholders.TutorialName")}
              value={search}
              setValue={setSearch}
              width="100%"
              height="48px"
            />
            <div className="ms-3 h-12 w-12">
              <button
                className="relative flex h-12  w-12 items-center justify-center rounded-full border border-darkGrey-200 bg-white py-2 text-base font-bold"
                onClick={() => setIsFiltersOpen(true)}
              >
                <HiAdjustments color="#2B3674" size="24" />
                <span
                  className="absolute flex h-4 w-4 items-center justify-center rounded-full bg-pink-400 text-[9px] text-white"
                  style={{ top: "-2px", right: "-2px" }}
                >
                  {getFiltersCount(filters)}
                </span>
              </button>
            </div>
          </div>
        )}

        <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
          {!isLoading ? (
            <>
              {tutorials.map((tutorial, index) => (
                <TutorialCard
                  tutorial={tutorial}
                  key={"tutorial" + index}
                  onClick={() => handleCardClick(tutorial)}
                />
              ))}
            </>
          ) : (
            <>
              {[...Array(12)].map((_, index) => (
                <INSkeleton key={"tutorial-skeleton" + index} />
              ))}
            </>
          )}
        </div>
      </div>
      {!isDesktop && (
        <MobileActivitiesFilters
          filters={filters}
          setFilters={setFilters}
          fetchActivities={fetchTutorials}
          activitiesType="tutorials"
          setIsFiltersOpen={setIsFiltersOpen}
          resetFilters={resetFilters}
        />
      )}
    </div>
  );
}
